import React from "react";
import {
  Table,
  Tbody,
  Tr,
  Td,
  TableContainer,
  Center,
} from "@chakra-ui/react";
import { useIntl } from "react-intl";
import "./IpCheckerTable.css";
import { BooleanItemComponent } from "../../ChechersUIComponents/BooleanItemComponent";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";

const IpCheckerTable = ({ title, data = [], type }: { title: string; data: any[]; type: string }) => {
  const intl = useIntl();

  // Convert country code to emoji flag
  const countryCodeToEmoji = (code?: string) => {
    if (!code) return '';
    const uppercaseCode = code.trim().toUpperCase();
    if (uppercaseCode.length !== 2) return '';
    const OFFSET = 127397; // Regional Indicator Symbol Letter A
    const chars = Array.from(uppercaseCode).map((c) => String.fromCodePoint((c.charCodeAt(0) || 65) + OFFSET));
    return chars.join('');
  };

  // Function to get translated field name
  const getFieldName = (key: string) => {
    // Remove data. prefix and other prefixes for translation lookup
    let cleanKey = key.replace(/^data\./, '').replace(/\[.*?\]/g, '');
    
    // Handle nested fields (e.g., threat.is_tor -> is_tor)
    // Take the last part after the dot
    if (cleanKey.includes('.')) {
      const parts = cleanKey.split('.');
      cleanKey = parts[parts.length - 1];
    }
    
    // Fix MDCountry -> Country issue
    if (cleanKey === 'MDCountry') {
      cleanKey = 'country';
    }
    
    // Fix other possible field name issues
    if (cleanKey.endsWith('Country') && cleanKey !== 'country') {
      cleanKey = 'country';
    }
    
    return intl.formatMessage({ id: cleanKey, defaultMessage: cleanKey });
  };

  // Function to format value
  const formatValue = (key: string, value: any) => {
    // Time formatting
    if (key.includes('current_time') || (key.includes('time') && typeof value === 'string' && value.includes('T'))) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString(intl.locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'
          });
        }
      } catch (e) {
        // If parsing failed, return as is
      }
    }

    // Truncate long values
    if (typeof value === 'string' && value.length > 50) {
      return value.substring(0, 47) + '...';
    }

    return value;
  };

  // Function to get field icon
  const getFieldIcon = (key: string, value: any) => {
    // No longer showing flag in title, it will be in country code value
    return null;
  };

  // Function to group fields by blocks
  const groupFieldsByBlocks = (obj: any) => {
    const blocks: any = {
      basic: {},      // Basic information (IP, continent, country, city)
      provider: {},   // Provider (organization, AS, hostname)
      network: {},    // Network information (route, domain, connection type)
      location: {},   // Geolocation (coordinates, postal code, region)
      time: {},       // Time and timezone
      currency: {},   // Currency
      language: {},   // Languages
      scores: {},     // Separate block for scores and risks
      other: {}       // Other fields
    };

    const processed = new Set();

    Object.keys(obj).forEach((key) => {
      if (processed.has(key) || key === 'status') return;

      const value = obj[key];
      // console.log(`🔍 [GROUPING] Processing key: ${key}, value:`, value);

      // Basic information
      if (['ip', 'continent', 'country', 'MDCountry', 'city', 'region'].includes(key)) {
        blocks.basic[key] = value;
        processed.add(key);
      }
      // Provider
      else if (['organization', 'org', 'asname', 'as', 'hostname', 'host'].includes(key)) {
        blocks.provider[key] = value;
        processed.add(key);
      }
      // Network information
      else if (['route', 'domain', 'connection', 'usageType', 'usage_type'].includes(key)) {
        blocks.network[key] = value;
        processed.add(key);
      }
      // Geolocation
      else if (['loc', 'location', 'postal', 'postal_code', 'zip'].includes(key)) {
        blocks.location[key] = value;
        processed.add(key);
      }
      // Time and timezone
      else if (key === 'time_zone' || key === 'current_time' || key === 'offset') {
        if (key === 'time_zone') {
          blocks.time.time_zone = { type: 'timezone', fields: value };
        } else {
          // current_time and offset are already included in time_zone object
          processed.add(key);
        }
        processed.add(key);
      }
      // Currency
      else if (key === 'currency') {
        blocks.currency.currency = { type: 'currency', fields: value };
        processed.add(key);
      }
      // Languages
      else if (key === 'languages') {
        blocks.language.languages = { type: 'languages', fields: value };
        processed.add(key);
      }
      // Scoring/risk fields -> separate block
      else if ([
        'fraud_score',
        'risk_score',
        'abuse_score',
        'abuse_confidence_score',
        'abuse_total_reports',
        'abuse_is_whitelisted',
        'scamalytics_score',
        'scamalytics_risk',
        'is_vpn', 'vpn',
        'is_proxy', 'proxy',
        'is_tor', 'tor',
        'is_bot',
        'recent_abuse',
        'is_known_attacker',
      ].includes(key)) {
        blocks.scores[key] = value;
        processed.add(key);
      }
      // Other fields
      else {
        blocks.other[key] = value;
        processed.add(key);
      }
    });

    return blocks;
  };

  return (
    <Center>
            <div style={{
              columnCount: 2,
              columnGap: 'var(--chakra-space-6)',
              width: '100%',
              maxWidth: '1200px',
              padding: 'var(--chakra-space-6)'
            }}>
        {data.map((item, index) => {
        // Extract data from API structure
        const actualData = item.data || item;
        
        // Step 1: Log full API response
        // console.log("🔍 [STEP 1] Full API response:", item);
        // console.log("🔍 [STEP 1] Extracted data:", actualData);
        // console.log("🔍 [STEP 1] Data keys:", Object.keys(actualData));
        
        // Group fields by blocks
        const blocks = groupFieldsByBlocks(actualData);
        // console.log("🔍 [STEP 2] Grouped blocks:", blocks);
        
        // Function to render a separate block as a container
        const renderBlockContainer = (blockName: string, blockData: any, blockTitle: string) => {
          if (Object.keys(blockData).length === 0) return null;
          
          return (
            <div key={blockName} style={{ 
              breakInside: 'avoid',
              marginBottom: 'var(--chakra-space-3)',
              width: '100%'
            }}>
      <BoxShadowContainer className="ip-checker-table-box-shadow-container">
                <div style={{ 
                  backgroundColor: 'var(--ips-surface-hover)',
                  fontWeight: 'bold', 
                  textAlign: 'center',
                  padding: 'var(--chakra-space-3)',
                  borderTop: '2px solid var(--ips-primary)',
                  borderBottom: '1px solid var(--ips-border)',
                  fontSize: 'var(--chakra-fontSizes-sm)',
                  color: 'var(--ips-text-secondary)',
                  borderRadius: 'var(--chakra-radii-lg) var(--chakra-radii-lg) 0 0',
                  transition: 'all 0.2s ease-in-out'
                }}>
                  {blockTitle}
                </div>
        <TableContainer className="ip-checker-table-container">
          <Table variant="simple" className="ip-checker-table">
            <Tbody>
                      {renderObject(blockData)}
                    </Tbody>
                  </Table>
                </TableContainer>
              </BoxShadowContainer>
            </div>
          );
                };
                
                // Recursive function to render nested objects
                const renderObject = (obj, prefix = '') => {
                  const rows = [];
                  
                  Object.keys(obj).forEach((key) => {
                    // Skip status field
                    if (key === 'status') {
                      return;
                    }
                    
                    const value = obj[key];
                    const fullKey = prefix ? `${prefix}.${key}` : key;
                    
                    // Special handling for grouped fields
                    if (value && typeof value === 'object' && value.type) {
                      if (value.type === 'currency') {
                        // Display currency in compact form
                        const currencyFields = value.fields;
                        const currencyDisplay = `${currencyFields.name || ''} (${currencyFields.code || ''}) ${currencyFields.symbol || ''}`;
                        rows.push(
                          <Tr key={`${index}-${fullKey}`}>
                            <Td className="response-key-item">
                              {getFieldIcon(fullKey, currencyFields.code)}
                              {getFieldName(fullKey)}
                            </Td>
                            <Td>{currencyDisplay}</Td>
                          </Tr>
                        );
                      } else if (value.type === 'timezone') {
                        // Display timezone in compact form
                        const timezoneFields = value.fields;
                        const timezoneDisplay = `${timezoneFields.name || ''} (${timezoneFields.abbr || ''}) ${timezoneFields.offset || ''}`;
                        rows.push(
                          <Tr key={`${index}-${fullKey}`}>
                            <Td className="response-key-item">
                              {getFieldIcon(fullKey, timezoneFields.name)}
                              {getFieldName(fullKey)}
                            </Td>
                            <Td>{timezoneDisplay}</Td>
                          </Tr>
                        );
                        
                        // Add separate row for current time if it exists
                        if (timezoneFields.current_time) {
                          const formattedTime = formatValue('current_time', timezoneFields.current_time);
                          rows.push(
                            <Tr key={`${index}-${fullKey}-current-time`}>
                              <Td className="response-key-item" style={{ paddingLeft: '20px' }}>
                                {intl.formatMessage({ id: "current_time", defaultMessage: "Current time" })}
                              </Td>
                              <Td>{formattedTime}</Td>
                            </Tr>
                          );
                        }
                        
                        // Add daylight saving time information if available
                        if (timezoneFields.is_dst !== undefined) {
                          rows.push(
                            <Tr key={`${index}-${fullKey}-is-dst`}>
                              <Td className="response-key-item" style={{ paddingLeft: '20px' }}>
                                {intl.formatMessage({ id: "is_dst", defaultMessage: "Daylight saving" })}
                              </Td>
                              <Td>
                                <BooleanItemComponent value={timezoneFields.is_dst} />
                              </Td>
                            </Tr>
                          );
                        }
                      } else if (value.type === 'languages') {
                        // Display languages in compact form
                        const languageFields = value.fields;
                        if (Array.isArray(languageFields) && languageFields.length > 0) {
                          // If it's an array of languages
                          const language = languageFields[0];
                          const languageDisplay = `${language.name || ''} (${language.native || ''})`;
                          rows.push(
                            <Tr key={`${index}-${fullKey}`}>
                              <Td className="response-key-item">
                                {getFieldIcon(fullKey, language.code)}
                                {getFieldName(fullKey)}
                              </Td>
                              <Td>{languageDisplay}</Td>
                            </Tr>
                          );
                        } else {
                          // If it's a language object
                          const languageDisplay = `${languageFields.name || ''} (${languageFields.native || ''})`;
                          rows.push(
                            <Tr key={`${index}-${fullKey}`}>
                              <Td className="response-key-item">
                                {getFieldIcon(fullKey, languageFields.code)}
                                {getFieldName(fullKey)}
                              </Td>
                              <Td>{languageDisplay}</Td>
                            </Tr>
                          );
                        }
                      }
                    } else if (value === null || value === undefined) {
                      // For boolean-like fields null/undefined show as "no" (false)
                      const booleanLikeFields = ['proxy', 'tor', 'vpn', 'is_vpn', 'is_proxy', 'is_tor', 'is_bot'];
                      const isBooleanLikeField = booleanLikeFields.some(field => 
                        key === field || fullKey.toLowerCase().includes(field.toLowerCase())
                      );
                      
                      if (isBooleanLikeField) {
                        // For boolean-like fields show "no" instead of N/A
                        rows.push(
                          <Tr key={`${index}-${fullKey}`}>
                            <Td className="response-key-item">
                              {getFieldIcon(fullKey, false)}
                              {getFieldName(fullKey)}
                            </Td>
                            <Td>
                              <BooleanItemComponent value={false} />
                            </Td>
                          </Tr>
                        );
                      } else {
                        // For other fields show N/A
                      rows.push(
                        <Tr key={`${index}-${fullKey}`}>
                          <Td className="response-key-item">
                            {getFieldIcon(fullKey, value)}
                            {getFieldName(fullKey)}
                          </Td>
                          <Td>N/A</Td>
                        </Tr>
                      );
                      }
                    } else if (typeof value === 'object' && !Array.isArray(value)) {
                      // Recursively process nested objects
                      const nestedRows = renderObject(value, fullKey);
                      rows.push(...nestedRows);
                    } else if (Array.isArray(value)) {
                      // Process arrays
                      if (value.length === 0) {
                        rows.push(
                          <Tr key={`${index}-${fullKey}`}>
                            <Td className="response-key-item">
                              {getFieldIcon(fullKey, value)}
                              {getFieldName(fullKey)}
                            </Td>
                            <Td>[]</Td>
                          </Tr>
                        );
                      } else if (typeof value[0] === 'object') {
                        // Array of objects
                        value.forEach((arrayItem, arrayIndex) => {
                          const arrayRows = renderObject(arrayItem, `${fullKey}[${arrayIndex}]`);
                          rows.push(...arrayRows);
                        });
                      } else {
                        // Array of primitives
                        rows.push(
                          <Tr key={`${index}-${fullKey}`}>
                            <Td className="response-key-item">
                              {getFieldIcon(fullKey, value)}
                              {getFieldName(fullKey)}
                            </Td>
                            <Td>{value.join(', ')}</Td>
                          </Tr>
                        );
                      }
                    } else if (typeof value === 'boolean') {
                      // Process boolean values
                      rows.push(
                        <Tr key={`${index}-${fullKey}`}>
                          <Td className="response-key-item">
                            {getFieldIcon(fullKey, value)}
                            {getFieldName(fullKey)}
                          </Td>
                          <Td>
                            <BooleanItemComponent value={value} />
                          </Td>
                        </Tr>
                      );
                    } else {
                      // Special handling for numeric values 0/1 for proxy/tor/vpn fields
                      const booleanLikeFields = ['proxy', 'tor', 'vpn', 'is_vpn', 'is_proxy', 'is_tor', 'is_bot'];
                      const isBooleanLikeField = booleanLikeFields.some(field => 
                        key === field || fullKey.toLowerCase().includes(field.toLowerCase())
                      );
                      
                      // If it's a numeric value 0/1 for boolean-like field, process as boolean
                      if (isBooleanLikeField && (value === 0 || value === 1 || value === '0' || value === '1')) {
                        const boolValue = value === 1 || value === '1';
                        rows.push(
                          <Tr key={`${index}-${fullKey}`}>
                            <Td className="response-key-item">
                              {getFieldIcon(fullKey, boolValue)}
                              {getFieldName(fullKey)}
                            </Td>
                            <Td>
                              <BooleanItemComponent value={boolValue} />
                          </Td>
                        </Tr>
                      );
                    } else {
                      // Process primitive values
                      const formattedValue = formatValue(fullKey, value);
                      const isCountryCode = ['country_code', 'countryCode'].includes(key) || /country[_-]?code$/i.test(fullKey);
                      const isCountry = key === 'country' || key === 'MDCountry' || key.endsWith('Country');
                      // For country code/name show flag next to value with emoji
                      rows.push(
                        <Tr key={`${index}-${fullKey}`}>
                          <Td className="response-key-item">
                            {getFieldIcon(fullKey, value)}
                            {getFieldName(fullKey)}
                          </Td>
                          <Td>
                            {(isCountry || isCountryCode) ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                <span>{countryCodeToEmoji(String(value))}</span>
                                <span>{formattedValue.toString()}</span>
                              </span>
                            ) : (
                              formattedValue.toString()
                            )}
                          </Td>
                        </Tr>
                      );
                      }
                    }
                  });
                  
                  return rows;
                };
                
        return (
          <React.Fragment key={`data-${index}`}>
            {renderBlockContainer('basic', blocks.basic, intl.formatMessage({ id: "BasicInfo", defaultMessage: "Basic Information" }))}
            {renderBlockContainer('provider', blocks.provider, intl.formatMessage({ id: "ProviderInfo", defaultMessage: "Provider Information" }))}
            {renderBlockContainer('network', blocks.network, intl.formatMessage({ id: "NetworkInfo", defaultMessage: "Network Information" }))}
            {renderBlockContainer('location', blocks.location, intl.formatMessage({ id: "LocationInfo", defaultMessage: "Location Information" }))}
            {renderBlockContainer('time', blocks.time, intl.formatMessage({ id: "TimeInfo", defaultMessage: "Time Information" }))}
            {renderBlockContainer('currency', blocks.currency, intl.formatMessage({ id: "CurrencyInfo", defaultMessage: "Currency Information" }))}
            {renderBlockContainer('language', blocks.language, intl.formatMessage({ id: "LanguageInfo", defaultMessage: "Language Information" }))}
            {renderBlockContainer('scores', blocks.scores, intl.formatMessage({ id: "ScoresInfo", defaultMessage: "Scores" }))}
            {renderBlockContainer('other', blocks.other, intl.formatMessage({ id: "OtherInfo", defaultMessage: "Other Information" }))}
          </React.Fragment>
        );
      })}
      </div>
    </Center>
  );
};

export default IpCheckerTable;